import { useEffect, useRef, useState } from 'react';
import Sketch from '@uiw/react-color-sketch';
import { Portal } from './Portal';

interface Props {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}

function parseHex(hex: string): { r: number; g: number; b: number; a: number } {
  const s = hex.replace('#', '');
  if (s.length === 6) {
    return {
      r: parseInt(s.slice(0, 2), 16),
      g: parseInt(s.slice(2, 4), 16),
      b: parseInt(s.slice(4, 6), 16),
      a: 1,
    };
  }
  if (s.length === 8) {
    return {
      r: parseInt(s.slice(0, 2), 16),
      g: parseInt(s.slice(2, 4), 16),
      b: parseInt(s.slice(4, 6), 16),
      a: parseInt(s.slice(6, 8), 16) / 255,
    };
  }
  return { r: 0, g: 0, b: 0, a: 1 };
}

export default function ColorPickerField({ label, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 12, top: 12 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  function positionPopover() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = 230;
    const panelHeight = 340;
    const left = Math.min(window.innerWidth - panelWidth - 12, Math.max(12, rect.left));
    const below = rect.bottom + 8;
    const top = below + panelHeight <= window.innerHeight
      ? below
      : Math.max(12, rect.top - panelHeight - 8);
    setPosition({ left, top });
  }

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (
        popRef.current
        && !popRef.current.contains(e.target as Node)
        && !triggerRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      positionPopover();
      document.addEventListener('mousedown', onClick);
      window.addEventListener('resize', positionPopover);
      window.addEventListener('scroll', positionPopover, true);
    }
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [open]);

  const rgba = parseHex(value);
  const alphaPct = Math.round(rgba.a * 100);
  const swatchRgba = `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;

  return (
    <div className="relative">
      <label className="ff-label">{label}</label>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          positionPopover();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="flex w-full items-center gap-3 rounded-xl border border-ff-line bg-white px-3 py-2 transition-colors hover:border-ff-orange/50"
      >
        <span
          className="h-6 w-6 rounded-md border border-ff-ink/10 shadow-inner shrink-0 relative overflow-hidden"
          style={{
            backgroundImage:
              'conic-gradient(rgba(0,0,0,0.18) 0% 25%, transparent 0% 50%, rgba(0,0,0,0.18) 0% 75%, transparent 0% 100%)',
            backgroundSize: '8px 8px',
          }}
        >
          <span
            className="absolute inset-0"
            style={{ background: swatchRgba }}
          />
        </span>
        <span className="font-mono text-xs text-ff-ink/80 uppercase">{value}</span>
        {rgba.a < 1 && (
          <span className="ml-auto font-mono text-[10px] text-ff-ink/60">{alphaPct}%</span>
        )}
      </button>
      {open && (
        <Portal>
          <div
            ref={popRef}
            role="dialog"
            aria-label={`${label} colour picker`}
            className="fixed z-[80] overflow-hidden rounded-xl border border-ff-line bg-white shadow-hover"
            style={position}
          >
            <Sketch
              color={value}
              disableAlpha={false}
              onChange={(c) => {
                const r = c.rgba.r.toString(16).padStart(2, '0');
                const g = c.rgba.g.toString(16).padStart(2, '0');
                const b = c.rgba.b.toString(16).padStart(2, '0');
                const a = Math.round(c.rgba.a * 255).toString(16).padStart(2, '0');
                const hex = c.rgba.a < 1 ? `#${r}${g}${b}${a}`.toUpperCase() : `#${r}${g}${b}`.toUpperCase();
                onChange(hex);
              }}
              presetColors={[
                '#FFFFFF',
                '#0B3D2E',
                '#214E3F',
                '#EBF4E9',
                '#E0382B',
                '#1B1F1C',
                '#FBF9F4',
                '#E5DCC8',
              ]}
            />
          </div>
        </Portal>
      )}
    </div>
  );
}
